import ArticleIcon from "@mui/icons-material/Article";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import "react-quill/dist/quill.snow.css";
import remarkGfm from "remark-gfm";
import { useLazyGetDocumentFileQuery } from "../../api/admin/chatbotApi";

const MessageTabs = ({ message, sources, rgbaColor }) => {
  const [tabValue, setTabValue] = useState(0);
  const [getDocumentFile] = useLazyGetDocumentFileQuery();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenPDF = async (documentId, pageNumber) => {
    try {
      // Ottieni il file PDF
      const result = await getDocumentFile(documentId);
      console.log("PDF caricato con successo:", result);

      if (result.data) {
        // Crea un URL temporaneo per il blob
        const blob = result.data;
        const url = URL.createObjectURL(blob);

        // Prova prima con il parametro #page=
        const urlWithPage = `${url}#page=${pageNumber}`;
        const newWindow = window.open(urlWithPage, "_blank");

        // Se la finestra non si apre (popup bloccato), mostra un messaggio
        if (!newWindow) {
          alert(
            "Popup bloccato. Abilita i popup per questo sito per aprire il PDF."
          );
          // Fallback: scarica il file
          const link = document.createElement("a");
          link.href = url;
          link.download = `documento_pagina_${pageNumber}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }

        // Pulisci l'URL dopo un po' di tempo per liberare memoria
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 5000); // Aumentato il tempo per dare modo al browser di caricare
      }
    } catch (error) {
      console.error("Errore durante il caricamento del PDF:", error);
      alert("Errore durante il caricamento del PDF");
    }
  };

  return (
    <Card
      style={{
        borderRadius: "20px",
        backgroundColor: message?.role === "user" ? "white" : rgbaColor,
      }}
      elevation={4}
    >
      <CardContent sx={{ padding: 0 }}>
        {message?.role === "assistant" ? (
          <>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": {
                  textTransform: "none",
                  minHeight: "48px",
                },
              }}
            >
              <Tab label="Risposta" />
              <Tab
                label={`Fonti ${
                  sources && sources.length > 0 ? `(${sources.length})` : ""
                }`}
                disabled={!sources || sources.length === 0}
              />
            </Tabs>

            <Box sx={{ padding: 2 }}>
              {tabValue === 0 && (
                <div className="text-black normal-case m-0">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                </div>
              )}

              {tabValue === 1 && (
                <div>
                  {sources && sources.length > 0 ? (
                    sources.map((source, sourceIndex) => (
                      <Accordion key={sourceIndex} sx={{ marginBottom: 1 }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            backgroundColor: "rgba(0,0,0,0.02)",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <ArticleIcon color="primary" />
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold" }}
                            >
                              {source.fileName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ({source.chunks?.length || 0} sezioni)
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {source.chunks &&
                            source.chunks.map((chunk, chunkIndex) => (
                              <Box
                                key={chunkIndex}
                                sx={{
                                  marginBottom: 2,
                                  padding: 1,
                                  backgroundColor: "rgba(0,0,0,0.02)",
                                  borderRadius: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 1,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="primary"
                                    sx={{
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Pagine: {chunk.pages?.join(", ") || "N/A"}
                                  </Typography>

                                  {/* Pulsante per aprire PDF */}
                                  {chunk.pages && chunk.pages.length > 0 && (
                                    <Tooltip
                                      title={`Apri PDF alla pagina ${chunk.pages[0]}`}
                                    >
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() =>
                                          handleOpenPDF(
                                            source.id,
                                            chunk.pages[0]
                                          )
                                        }
                                        sx={{
                                          padding: 0.5,
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(25, 118, 210, 0.1)",
                                          },
                                        }}
                                      >
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.875rem",
                                    lineHeight: 1.5,
                                    maxHeight: "200px",
                                    overflow: "auto",
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {chunk.text}
                                </Typography>
                              </Box>
                            ))}
                        </AccordionDetails>
                      </Accordion>
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        fontStyle: "italic",
                        textAlign: "center",
                        padding: 2,
                      }}
                    >
                      Nessuna fonte disponibile per questa risposta
                    </Typography>
                  )}
                </div>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ padding: 2 }}>
            <div className="text-black normal-case m-0">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content}
              </ReactMarkdown>
            </div>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MessageTabs;
